import { useEffect, useRef, useState } from 'react';
import { FormModal } from './FormModal';
import { buildTechnicalMetadata, submitBetaFeedbackForm } from './betaFeedback';
import {agentContentSettings} from '../content';

type FeedbackValues = {
  name: string;
  email: string;
  feedback: string;
  website: string;
};

type FeedbackModalProps = {
  open: boolean;
  endpoint: string;
  getThreadId: () => string | undefined;
  onClose: () => void;
};

const EMPTY_VALUES: FeedbackValues = { name: '', email: '', feedback: '', website: '' };

function validate(values: FeedbackValues) {
  const copy = agentContentSettings.feedbackForms.feedback.validation;
  const errors: Partial<Record<keyof FeedbackValues, string>> = {};
  if (!values.name.trim()) errors.name = copy.nameRequired;
  if (!values.email.trim()) errors.email = copy.emailRequired;
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = copy.emailInvalid;
  if (!values.feedback.trim()) errors.feedback = copy.feedbackRequired;
  if (values.name.length > 100 || values.email.length > 254 || values.feedback.length > 3000) {
    const invalidField = values.name.length > 100 ? 'name' : values.email.length > 254 ? 'email' : 'feedback';
    errors[invalidField] = copy.tooLong;
  }
  return errors;
}

export function FeedbackModal({ open, endpoint, getThreadId, onClose }: FeedbackModalProps) {
  const copy = agentContentSettings.feedbackForms.feedback;
  const [values, setValues] = useState<FeedbackValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Partial<Record<keyof FeedbackValues, string>>>({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef<Partial<Record<keyof FeedbackValues, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const submissionLock = useRef(false);

  const reset = () => {
    setValues(EMPTY_VALUES);
    setErrors({});
    setAttempted(false);
    setSubmitting(false);
    setSubmissionError('');
    setSuccess(false);
    submissionLock.current = false;
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const updateValue = (field: keyof FeedbackValues, value: string) => {
    const nextValues = { ...values, [field]: value };
    setValues(nextValues);
    if (attempted || errors[field]) {
      const nextErrors = validate(nextValues);
      setErrors(current => ({ ...current, [field]: nextErrors[field] }));
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || submissionLock.current) return;

    const trimmed: FeedbackValues = {
      name: values.name.trim(),
      email: values.email.trim(),
      feedback: values.feedback.trim(),
      website: values.website,
    };
    const nextErrors = validate(trimmed);
    setAttempted(true);
    setErrors(nextErrors);
    setSubmissionError('');

    const firstInvalid = (['name', 'email', 'feedback'] as const).find(field => nextErrors[field]);
    if (firstInvalid) {
      requestAnimationFrame(() => inputRefs.current[firstInvalid]?.focus());
      return;
    }
    if (trimmed.website) return;

    setValues(trimmed);
    submissionLock.current = true;
    setSubmitting(true);
    try {
      await submitBetaFeedbackForm({
        endpoint,
        values: {
          name: trimmed.name,
          email: trimmed.email,
          feedback: trimmed.feedback,
        },
        metadata: buildTechnicalMetadata({
          submissionType: 'Feedback',
          threadId: getThreadId(),
          agentName: agentContentSettings.defaultAgentName,
        }),
      });
      setValues(EMPTY_VALUES);
      setErrors({});
      setSuccess(true);
    } catch {
      setSubmissionError(copy.validation.submitFailed);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      id="feedback"
      title={copy.title}
      intro={copy.intro}
    >
      {success ? (
        <div className="account-form__confirmation-container" role="status">
          <p className="account-font__body-s twe-asa-form-success-message">Thank you for your feedback. It has been shared with our team.</p>
          <div className="account-form__cta-container--centered">
            <button type="button" className="cta-button cta-button--delta" data-form-autofocus onClick={handleCancel}>Close Window</button>
          </div>
        </div>
      ) : (
        <form className="account-form__form" noValidate onSubmit={onSubmit}>
          <ul className="tweForm-content">
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-input" htmlFor="twe-asa-feedback-name">{copy.nameLabel}</label>
              <input
                ref={element => { inputRefs.current.name = element; }}
                id="twe-asa-feedback-name"
                className={`tweForm-input form-inputs__text-input${errors.name ? ' form-inputs__text-input--error' : ''}`}
                name="name"
                type="text"
                required
                maxLength={100}
                autoComplete="name"
                value={values.name}
                onChange={event => updateValue('name', event.target.value)}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'twe-asa-feedback-name-error' : undefined}
                data-form-autofocus
              />
              {errors.name && <span id="twe-asa-feedback-name-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.name}</span>}
            </li>
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-input" htmlFor="twe-asa-feedback-email">{copy.emailLabel}</label>
              <input
                ref={element => { inputRefs.current.email = element; }}
                id="twe-asa-feedback-email"
                className={`tweForm-input form-inputs__text-input${errors.email ? ' form-inputs__text-input--error' : ''}`}
                name="email"
                type="email"
                required
                maxLength={254}
                autoComplete="email"
                value={values.email}
                onChange={event => updateValue('email', event.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'twe-asa-feedback-email-error' : undefined}
              />
              {errors.email && <span id="twe-asa-feedback-email-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.email}</span>}
            </li>
            <li className="tweForm-item form-inputs__container form-inputs__container--flex-column">
              <label className="tweForm-label form-inputs__label form-inputs__label--text-area" htmlFor="twe-asa-feedback">{copy.feedbackLabel}</label>
              <textarea
                ref={element => { inputRefs.current.feedback = element; }}
                id="twe-asa-feedback"
                className={`tweForm-input form-inputs__text-area${errors.feedback ? ' form-inputs__text-input--error' : ''}`}
                name="feedback"
                required
                maxLength={3000}
                rows={7}
                value={values.feedback}
                onChange={event => updateValue('feedback', event.target.value)}
                aria-invalid={Boolean(errors.feedback)}
                aria-describedby={`${errors.feedback ? 'twe-asa-feedback-error ' : ''}twe-asa-feedback-counter`.trim()}
              />
              {errors.feedback && <span id="twe-asa-feedback-error" className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{errors.feedback}</span>}
              <p id="twe-asa-feedback-counter" className="account-font__body-xs" aria-live="polite">{values.feedback.length} / 3000</p>
            </li>
          </ul>
          <div className="twe-asa-form-honeypot" aria-hidden="true">
            <label htmlFor="twe-asa-feedback-website">{copy.websiteLabel}</label>
            <input
              ref={element => { inputRefs.current.website = element; }}
              id="twe-asa-feedback-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values.website}
              onChange={event => updateValue('website', event.target.value)}
            />
          </div>
          <div className="tweForm-item form-inputs__container form-inputs__container--flex-column account-form__confirmation-container">
            <p className="account-font__body-xs">{copy.privacyNotice}</p>
            <p className="account-font__body-xs">{copy.privacyPrefix} <a href={copy.privacyLinkUrl} target="_blank" rel="noopener noreferrer">{copy.privacyLinkText}</a>.</p>
          </div>
          {submissionError && <span className="tweForm-required form-inputs__label form-inputs__label--text-input form-inputs__label--error" role="alert">{submissionError}</span>}
          <div className="account-form__cta-container--centered">
            <button type="submit" className={`cta-button cta-button--alpha${submitting ? ' cta-button--disabled' : ''}`} disabled={submitting}>{submitting ? copy.submittingButtonText : copy.submitButtonText}</button>{' '}
            <button type="button" className="cta-button cta-button--delta" onClick={handleCancel}>{copy.cancelButtonText}</button>
          </div>
        </form>
      )}
    </FormModal>
  );
}
